/**
 * Shared account-name / account-list label helper.
 *
 * Several surfaces need to name an account consistently. Historically each
 * screen carried its own `ACCOUNT_TYPE_LABEL` map and inline label logic, which
 * drifted: the "Transfer Between Accounts" dropdown already showed the friendly
 * product name (the account `Nickname`, e.g. "Explore Account", "21 Day Notice
 * Account"), while the customer dashboard tiles still showed only the generic
 * account-type bucket ("Current Account" / "Deposit Account"). This helper is
 * the single source of truth for that naming convention so every surface reads
 * the same (issues #380 dashboard tiles, #381 account-list dropdowns).
 *
 * Canonical name: `Nickname` → friendly type label → raw account-type code.
 * Canonical dropdown option label: `<name> <last4>` (mirrors the Transfer
 * Between Accounts dropdown). This standardises the *label*; it does not change
 * masking — only the last 4 account digits are ever shown to the agent (CCF-80,
 * #130/#131 won't-fix).
 *
 * Defensive by design (issue #262): the real PTSB gateway can return an account
 * with no parseable account number, so `AccountNumber` can arrive
 * `undefined`/null/non-string. Coercing to a string first keeps the label total
 * — a missing number yields just the name rather than throwing during render.
 */

/** Friendly labels for the account-type codes the gateway returns. */
export const ACCOUNT_TYPE_LABEL: Record<string, string> = {
  CURRENT: 'Current Account',
  DEPOSIT: 'Deposit Account',
  LOAN: 'Loan Account',
  CARD: 'Credit Card',
};

/** The minimal account shape every label needs. */
export interface AccountLabelInput {
  AccountType?: string;
  AccountNumber?: unknown;
  Nickname?: string;
}

/** The friendly display name for an account: nickname → type label → raw code. */
export function accountName(account: AccountLabelInput): string {
  const nickname =
    typeof account.Nickname === 'string' ? account.Nickname.trim() : '';
  return (
    nickname ||
    (account.AccountType && ACCOUNT_TYPE_LABEL[account.AccountType]) ||
    account.AccountType ||
    'Account'
  );
}

/**
 * The canonical account-list dropdown option label: `<name> <last4>`.
 *
 * Matches the Transfer Between Accounts dropdown. If the account number is
 * missing/unparseable the label is just the name (no dangling separator).
 */
export function accountOptionLabel(account: AccountLabelInput): string {
  const name = accountName(account);
  const number =
    typeof account.AccountNumber === 'string' ? account.AccountNumber : '';
  const last4 = number.slice(-4);
  return last4 ? `${name} ${last4}` : name;
}
